import React, { useEffect, useRef, useState } from "react";
import { theme } from "antd";

interface StickyBarProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Distance from the top of the scroll container. */
  top?: number;
  /** Above the content it covers; raise it if it shares a stacking context. */
  zIndex?: number;
}

/**
 * A bar that stays at the top of its scroll container, and casts a shadow onto
 * whatever passes underneath it — but only once something actually does.
 *
 * The shadow is the only signal that content is hidden behind the bar rather
 * than simply ending there, so it must not be painted while the bar is sitting
 * at the top of an unscrolled page: a permanent shadow reads as a border.
 *
 * Whether it is stuck is answered by a zero-height sentinel rendered just above
 * it: while the sentinel is on screen the bar is at rest, and when it scrolls
 * out the bar has taken its place. That works whichever ancestor is doing the
 * scrolling, which a scroll listener cannot do without being handed the
 * container.
 *
 * The app header in Layout shows the same shadow by a different route — it is
 * not sticky at all, but a fixed row above a scrolling box, so it reads that
 * box's scrollTop directly.
 */
/**
 * The same shadow for a bar that is not sticky at all: one that sits above a
 * box which scrolls, so content passes under its bottom edge rather than under
 * a stuck element. There is nothing for a sentinel to watch in that shape, so
 * the box reports its own scrollTop.
 *
 * Give `onScroll` to the scrolling box and `shadow` to the bar above it.
 */
export function useScrolledUnder() {
  const [scrolled, setScrolled] = useState(false);
  const {
    token: { boxShadowTertiary, motionDurationSlow },
  } = theme.useToken();

  return {
    onScroll: (event: React.UIEvent<HTMLElement>) =>
      setScrolled(event.currentTarget.scrollTop > 0),
    shadow: {
      boxShadow: scrolled ? boxShadowTertiary : undefined,
      transition: `box-shadow ${motionDurationSlow}`,
    } as React.CSSProperties,
  };
}

export default function StickyBar({
  children,
  className,
  style,
  top = 0,
  zIndex = 3,
}: StickyBarProps): React.ReactNode {
  const sentinel = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  const {
    token: { boxShadowTertiary, motionDurationSlow },
  } = theme.useToken();

  useEffect(() => {
    const element = sentinel.current;

    if (!element || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) =>
      setStuck(!entry.isIntersecting),
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinel} aria-hidden style={{ height: 0 }} />
      <div
        className={className}
        style={{
          position: "sticky",
          top,
          zIndex,
          boxShadow: stuck ? boxShadowTertiary : undefined,
          transition: `box-shadow ${motionDurationSlow}`,
          ...style,
        }}
      >
        {children}
      </div>
    </>
  );
}
